import {
  Archive,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  Cog,
  Database,
  HardDrive,
  ListChecks,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useSystemStatus, type HealthLevel } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ServerStatusCard } from "@/features/dashboard/ServerStatusCard";
import { errorText } from "@/lib/errors";
import { formatShortDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const levelIcon: Record<HealthLevel, LucideIcon> = {
  ok: CircleCheck,
  warning: CircleAlert,
  error: CircleAlert,
  unknown: CircleHelp,
};

const levelBadge = {
  ok: "success",
  warning: "warning",
  error: "danger",
  unknown: "neutral",
} as const;

/** "1,2 ГБ" with the locale's units. */
function formatBytes(bytes: number, lang: string): string {
  const units = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return new Intl.NumberFormat(lang, {
    style: "unit",
    unit: units[i],
    unitDisplay: "short",
    maximumFractionDigits: value < 10 && i > 0 ? 1 : 0,
  }).format(value);
}

/** Health of the installation for administrators. */
export function ServerPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const status = useSystemStatus();
  const s = status.data;
  const when = (v: string) => formatShortDateTime(v, lang);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle>{t("settings.server.title")}</CardTitle>
          <CardDescription>
            {t("settings.server.description")}
            {s && ` ${t("settings.server.checked", { time: when(s.checked_at) })}`}
          </CardDescription>
        </CardHeader>
        {status.isError ? (
          <Alert variant="danger" className="mx-5 mb-5">
            {errorText(t, status.error)}
          </Alert>
        ) : !s ? (
          <Skeleton className="mx-5 mb-5 h-48" />
        ) : (
          <ul className="divide-y border-t">
            <Check
              icon={Cog}
              title={t("settings.server.worker.title")}
              level={s.worker.status}
              hint={s.worker.status !== "ok" ? t("settings.server.worker.hint") : undefined}
            >
              {s.worker.last_heartbeat_at
                ? t("settings.server.worker.ok", { time: when(s.worker.last_heartbeat_at) })
                : t("settings.server.worker.never")}
            </Check>
            <Check icon={ListChecks} title={t("settings.server.queue.title")} level={s.queue.status}>
              {t("settings.server.queue.text", {
                pending: s.queue.pending,
                failed: s.queue.failed_24h,
                backlog: s.queue.stats_backlog,
              })}
            </Check>
            <Check
              icon={Archive}
              title={t("settings.server.backup.title")}
              level={s.backup.status}
              hint={t("settings.server.backup.hint")}
            >
              {s.backup.status === "unknown"
                ? t("settings.server.backup.notConfigured")
                : s.backup.last_at
                  ? `${t("settings.server.backup.ok", {
                      time: when(s.backup.last_at),
                      size: formatBytes(s.backup.last_size_bytes ?? 0, lang),
                      total: s.backup.count,
                    })}${s.backup.status === "error" ? ` · ${t("settings.server.backup.stale")}` : ""}`
                  : t("settings.server.backup.none")}
            </Check>
            <Check icon={HardDrive} title={t("settings.server.disk.title")} level={s.disk.status}>
              {s.disk.free_bytes !== undefined && s.disk.total_bytes !== undefined
                ? t("settings.server.disk.text", {
                    free: formatBytes(s.disk.free_bytes, lang),
                    total: formatBytes(s.disk.total_bytes, lang),
                  })
                : t("settings.server.disk.unknown")}
            </Check>
            <Check icon={Database} title={t("settings.server.database.title")} level="ok">
              {t("settings.server.database.text", { size: formatBytes(s.database_bytes, lang) })}
            </Check>
          </ul>
        )}
      </Card>
      <ServerStatusCard />
    </div>
  );
}

type CheckProps = {
  icon: LucideIcon;
  title: string;
  level: HealthLevel;
  hint?: string;
  children: ReactNode;
};

function Check({ icon: Icon, title, level, hint, children }: CheckProps) {
  const { t } = useTranslation();
  const LevelIcon = levelIcon[level];
  return (
    <li className="flex items-start gap-4 px-5 py-4">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-muted">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{title}</div>
        <div className="text-muted">{children}</div>
        {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
      </div>
      <Badge variant={levelBadge[level]} className="shrink-0">
        <LevelIcon className={cn("size-3.5")} aria-hidden="true" />
        {t(`settings.server.levels.${level}`)}
      </Badge>
    </li>
  );
}
